import React, { useState } from "react";
import {
  Code,
  Plug,
  Zap,
  Users,
  Clock,
  Shield,
  ChevronRight,
  Copy,
  Check,
  Star,
  Download,
  ExternalLink,
  Search,
  Filter,
  Terminal,
  Database,
  Activity,
} from "lucide-react";

const ConnexallMarketplace = () => {
  const [activeTab, setActiveTab] = useState("rest");
  const [copiedCode, setCopiedCode] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const handleCopy = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(""), 2000);
  };

  const codeExamples = {
    rest: `// Connexall REST API Example
const response = await fetch('https://api.connexall.com/v1/patients', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

const patients = await response.json();
console.log(patients);`,
    fhir: `// FHIR R4 Patient Resource Example
const patient = {
  resourceType: "Patient",
  id: "example",
  identifier: [{
    use: "usual",
    type: {
      coding: [{
        system: "http://terminology.hl7.org/CodeSystem/v2-0203",
        code: "MR"
      }]
    },
    system: "urn:oid:1.2.36.146.595.217.0.1",
    value: "12345"
  }],
  active: true,
  name: [{
    use: "official",
    family: "Chalmers",
    given: ["Peter", "James"]
  }]
};`,
    mqtt: `// MQTT Client Connection Example
const mqtt = require('mqtt');
const client = mqtt.connect('mqtt://broker.connexall.com', {
  clientId: 'healthcare-system-001',
  username: 'YOUR_USERNAME',
  password: 'YOUR_PASSWORD'
});

client.on('connect', () => {
  console.log('Connected to Connexall MQTT broker');
  
  // Subscribe to patient monitoring topic
  client.subscribe('hospital/+/patient/+/vitals', (err) => {
    if (!err) {
      console.log('Subscribed to patient vitals');
    }
  });
});

client.on('message', (topic, message) => {
  const vitals = JSON.parse(message.toString());
  console.log('Received vitals:', vitals);
});`,
  };

  const plugins = [
    {
      id: 1,
      name: "Epic EHR Connector",
      vendor: "Epic Systems",
      category: "EHR",
      rating: 4.8,
      installs: 2340,
      verified: true,
      description:
        "Seamless integration with Epic's comprehensive EHR platform",
      lastUpdated: "2 days ago",
    },
    {
      id: 2,
      name: "Philips Patient Monitor Bridge",
      vendor: "Philips Healthcare",
      category: "Medical Devices",
      rating: 4.6,
      installs: 1890,
      verified: true,
      description:
        "Real-time vital signs streaming from Philips IntelliVue monitors",
      lastUpdated: "1 week ago",
    },
    {
      id: 3,
      name: "Cerner PowerChart Sync",
      vendor: "Community",
      category: "EHR",
      rating: 4.5,
      installs: 1567,
      verified: false,
      description: "Bi-directional data sync with Cerner PowerChart",
      lastUpdated: "3 days ago",
    },
    {
      id: 4,
      name: "GE Carescape Gateway",
      vendor: "GE Healthcare",
      category: "Medical Devices",
      rating: 4.7,
      installs: 2100,
      verified: true,
      description: "Connect GE Carescape monitors and anesthesia machines",
      lastUpdated: "5 days ago",
    },
    {
      id: 5,
      name: "HL7 to FHIR Transformer",
      vendor: "Community",
      category: "Data Transform",
      rating: 4.4,
      installs: 3200,
      verified: false,
      description: "Convert legacy HL7 v2 messages to modern FHIR resources",
      lastUpdated: "1 day ago",
    },
  ];

  const filteredPlugins = plugins.filter((plugin) => {
    const matchesSearch =
      plugin.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plugin.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || plugin.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-900 text-gray-400">
      {/* Header */}
      <header className="border-b border-gray-800 backdrop-blur-sm bg-gray-900/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-2">
                <Zap className="w-8 h-8 text-cyan-400" />
                <span className="text-xl font-bold">Connexall Marketplace</span>
              </div>
              <nav className="hidden md:flex space-x-6">
                <a
                  href="#"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Documentation
                </a>
                <a
                  href="#"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  API Reference
                </a>
                <a
                  href="#"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Community
                </a>
                <a
                  href="#"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Support
                </a>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <button className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors">
                Sign In
              </button>
              <button className="px-4 py-2 text-sm font-medium bg-cyan-500 hover:bg-cyan-400 text-gray-900 rounded-lg transition-colors">
                Get Started
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-purple-500/10"></div>
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
            Connect Once, Integrate Everywhere
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Turn weeks of custom integration work into minutes. Publish your
            adaptor once and let healthcare systems enable it with a single
            click.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-gray-900 font-medium rounded-lg transition-all transform hover:scale-105 flex items-center gap-2">
              <Plug className="w-5 h-5" />
              Browse Integrations
            </button>
            <button className="px-6 py-3 border border-gray-700 hover:border-gray-600 rounded-lg transition-all flex items-center gap-2">
              <Code className="w-5 h-5" />
              View Documentation
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 border-y border-gray-800">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="text-3xl font-bold text-cyan-400">150+</div>
            <div className="text-gray-400 mt-1">Active Integrations</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-400">20-30</div>
            <div className="text-gray-400 mt-1">New Systems/Year</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-400">5 min</div>
            <div className="text-gray-400 mt-1">Average Setup Time</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-400">99.9%</div>
            <div className="text-gray-400 mt-1">Uptime SLA</div>
          </div>
        </div>
      </section>

      {/* API Endpoints Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Powerful APIs & Protocols
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Choose from REST, FHIR, or MQTT protocols. Auto-generated code
              samples get you started in seconds.
            </p>
          </div>

          <div className="bg-gray-800 rounded-xl p-6">
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setActiveTab("rest")}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === "rest"
                    ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/50"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                <Terminal className="w-4 h-4 inline mr-2" />
                REST API
              </button>
              <button
                onClick={() => setActiveTab("fhir")}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === "fhir"
                    ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/50"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                <Database className="w-4 h-4 inline mr-2" />
                FHIR R4
              </button>
              <button
                onClick={() => setActiveTab("mqtt")}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === "mqtt"
                    ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/50"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                <Activity className="w-4 h-4 inline mr-2" />
                MQTT
              </button>
            </div>

            <div className="relative">
              <pre className="bg-gray-900 p-6 rounded-lg overflow-x-auto">
                <code className="text-sm text-gray-300">
                  {codeExamples[activeTab as keyof typeof codeExamples]}
                </code>
              </pre>
              <button
                onClick={() =>
                  handleCopy(
                    codeExamples[activeTab as keyof typeof codeExamples],
                    activeTab
                  )
                }
                className="absolute top-4 right-4 p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              >
                {copiedCode === activeTab ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Plugin Gallery */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-200">
              Community Plugin Gallery
            </h2>
            <p className="text-gray-200 max-w-2xl mx-auto">
              Discover pre-built integrations from vendors and the community.
              Enable with one click.
            </p>
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search plugins..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors"
            >
              <option value="all">All Categories</option>
              <option value="EHR">EHR Systems</option>
              <option value="Medical Devices">Medical Devices</option>
              <option value="Data Transform">Data Transform</option>
            </select>
          </div>

          {/* Plugin Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPlugins.map((plugin) => (
              <div
                key={plugin.id}
                className="bg-gray-800 rounded-xl p-6 hover:border-cyan-500/50 border border-gray-700 transition-all hover:transform hover:scale-105"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      {plugin.name}
                      {plugin.verified && (
                        <Shield
                          className="w-4 h-4 text-cyan-400"
                          title="Verified"
                        />
                      )}
                    </h3>
                    <p className="text-sm text-gray-400">{plugin.vendor}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span className="text-sm">{plugin.rating}</span>
                  </div>
                </div>

                <p className="text-gray-300 mb-4 text-sm">
                  {plugin.description}
                </p>

                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs text-gray-500">
                    {plugin.installs.toLocaleString()} installs
                  </span>
                  <span className="text-xs text-gray-500">
                    {plugin.lastUpdated}
                  </span>
                </div>

                <div className="flex gap-2">
                  <button className="flex-1 px-3 py-2 bg-cyan-500 hover:bg-cyan-400 text-gray-900 font-medium rounded-lg transition-colors text-sm">
                    Enable
                  </button>
                  <button className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors">
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-cyan-400">
            Why Build on Connexall?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Save Engineering Time
              </h3>
              <p className="text-gray-400">
                Stop building custom integrations for each customer. Publish
                once, deploy everywhere.
              </p>
            </div>
            <div className="bg-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Grow Your Ecosystem
              </h3>
              <p className="text-gray-400">
                Join a thriving community of healthcare vendors and developers
                building the future.
              </p>
            </div>
            <div className="bg-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Enterprise Security
              </h3>
              <p className="text-gray-400">
                HIPAA compliant, SOC 2 certified, and built with healthcare
                security requirements in mind.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-cyan-500/10 to-purple-500/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4 text-cyan-400">
            Ready to Transform Healthcare Integration?
          </h2>
          <p className="text-xl text-cyan-400 mb-8">
            Join 150+ vendors already saving time and increasing customer
            satisfaction.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-gray-900 font-medium rounded-lg transition-all transform hover:scale-105 flex items-center gap-2">
              Start Building
              <ChevronRight className="w-5 h-5" />
            </button>
            <button className="px-8 py-4 border border-gray-700 hover:border-gray-600 rounded-lg transition-all text-cyan-400">
              Schedule Demo
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-800">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Zap className="w-6 h-6 text-cyan-400" />
              <span className="text-lg font-bold text-cyan-400">Connexall</span>
            </div>
            <p className="text-sm text-cyan-400">
              The healthcare integration marketplace that turns bottlenecks into
              ecosystems.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-4">Developers</h3>
            <ul className="space-y-2 text-sm text-cyan-400">
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  API Documentation
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  SDK Downloads
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Code Examples
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Developer Forum
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-4">Company</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  About Us
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Partners
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Careers
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Contact
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-4">Legal</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Terms of Service
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  HIPAA Compliance
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Security
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm text-gray-400">
          © 2025 Connexall. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default ConnexallMarketplace;
